import React, { useState } from 'react';
import {
  Box, HStack, Icon, Text,
} from '@gluestack-ui/themed';
import { X } from 'lucide-react-native';
import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';

import type { BannerTone } from '@/common/oralExam/faultPresentation';

export type OperationalFaultStudentBannerProps = {
  copyKey: string;
  defaultValue: string;
  tone: BannerTone;
  testID?: string;
};

const BANNER_STYLE: Record<BannerTone, { bg: string; borderColor: string; textColor: string }> = {
  warning: {
    bg: '$statusWarningSubtle',
    borderColor: '$statusWarning',
    textColor: '$textBody',
  },
  info: {
    bg: '$bgBrandSubtle',
    borderColor: '$borderLight',
    textColor: '$textBody',
  },
};

/**
 * Dismissible, non-blocking student banner for operational faults (S6).
 * Follows the SupervisorBroadcastBanner placement and dismiss pattern.
 */
export function OperationalFaultStudentBanner({
  copyKey,
  defaultValue,
  tone,
  testID = 'oral-exam-operational-fault-banner',
}: OperationalFaultStudentBannerProps) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const style = BANNER_STYLE[tone];

  if (dismissed) return null;

  return (
    <Box
      bg={style.bg}
      borderBottomWidth={1}
      borderColor={style.borderColor}
      px="$4"
      py="$3"
      testID={testID}
    >
      <HStack space="sm" alignItems="flex-start" justifyContent="space-between">
        <Text flex={1} size="sm" color={style.textColor} testID={`${testID}-text`}>
          {t(copyKey, { defaultValue })}
        </Text>
        <Pressable
          onPress={() => setDismissed(true)}
          accessibilityRole="button"
          accessibilityLabel={t('common.buttons.dismiss', { defaultValue: 'Dismiss' })}
          testID={`${testID}-dismiss`}
        >
          <Icon as={X} size="sm" color="$textSubtle" />
        </Pressable>
      </HStack>
    </Box>
  );
}
